"use client";

import dynamic from "next/dynamic";
import { createContext, useContext, useState, type FocusEvent, type PointerEvent } from "react";

import BirthToolFormShell, {
  type BirthToolFormShellProps,
} from "@/components/tool-shell/BirthToolFormShell";

import type { DrishtiOffsets } from "./graha-chart";
import {
  GRAHA_CHART_CHECK_FIELD_IDS,
  GRAHA_CHART_CHECK_FORM,
  GRAHA_CHART_CHECK_FRAME,
  GRAHA_CHART_CHECK_HI_FORM,
  GRAHA_CHART_FORM_RESERVE,
} from "./graha-chart-check-form";

// The /graha/ hub check's one boundary (the /yoga/ hub's pattern,
// YogaHubCheckLazy): the hub's section imports only this module, and it owns
// the one dynamic() to the island. The form, place search, kundali client,
// chart reader, answer and analytics load as an async chunk; the first load
// gains only this file and the shell.
//
// Activation is element-scoped and local: no window listener and no timer, so
// a reader scrolling the hub never downloads the island; a press, focus or
// submit on the shell does.
//
// The slot reserves the form's tallest state, so neither shell -> form nor an
// engine error after a slow reply moves the graha cards below; the answer's
// taller room is added by the island in the press that sends the request
// (GRAHA_CHART_ANSWER_RESERVE, the career check's pattern). Natural heights
// measured 2026-10-20 (next dev + Playwright on /graha/, 7 widths from 320 to
// 1280, service workers blocked, POST /kundali mocked with a timed answer, a
// date-only answer and an engine failure): the shell and the empty form are
// 436 at 320-390 px wide, 420 at 414-480 and 362 from 640; with the field
// errors of an empty submit 478 at 320-390, 462 at 414-480 and 384 from 640;
// with "Don't know your birth time?" open 538 at 320-360, 522 at 390, 506 at
// 414, 490 at 480 and 496 from 640; the engine-failure alert stays within the
// empty form's height. Hence 600 px on phones and 540 from the sm breakpoint
// (GRAHA_CHART_FORM_RESERVE), room for the toggle and the field errors
// together. With them the graha cards never moved and no layout shift was
// recorded outside the reader's own input in any run.
// The /hi/graha/ hub mounts the same slot with language="hi": the same form
// component with the Hinglish card (GRAHA_CHART_CHECK_HI_FORM) and the same
// floors, since the transit hubs' measurements found the Hindi card's shell and
// form no taller than the English one at any width.
// translate="no" covers the shell, form and answer, which re-render on "Check
// another birth date" (tests/translate-safe-islands.test.ts).

type CheckLanguage = "en" | "hi";

/** Each locale's shell: the live form's words in the shared frame. */
const SHELL: Readonly<Record<CheckLanguage, BirthToolFormShellProps>> = {
  en: { ...GRAHA_CHART_CHECK_FORM, className: GRAHA_CHART_CHECK_FRAME },
  hi: { ...GRAHA_CHART_CHECK_HI_FORM, className: GRAHA_CHART_CHECK_FRAME },
};

/**
 * The shell's props for this page's language. dynamic()'s loading placeholder
 * receives no props from the page, so the slot hands them down through
 * context (the transit hubs' TransitsMoonCheckLazy).
 */
const ShellProps = createContext<BirthToolFormShellProps>(SHELL.en);

function LoadingShell() {
  return <BirthToolFormShell {...useContext(ShellProps)} />;
}

const GrahaChartCheck = dynamic(() => import("./GrahaChartCheck"), {
  ssr: false,
  loading: () => <LoadingShell />,
});

/** BirthToolFormShell input names -> the real form's input ids. */
const SHELL_FIELD_IDS: Readonly<Record<string, string>> = {
  dob: GRAHA_CHART_CHECK_FIELD_IDS.date,
  tob: GRAHA_CHART_CHECK_FIELD_IDS.time,
  place: GRAHA_CHART_CHECK_FIELD_IDS.place,
};

function formFieldId(target: EventTarget | null): string {
  const name = target instanceof Element ? target.getAttribute("name") : null;
  return (name && SHELL_FIELD_IDS[name]) || GRAHA_CHART_CHECK_FIELD_IDS.date;
}

export default function GrahaChartCheckLazy({
  offsets,
  language = "en",
}: {
  offsets: DrishtiOffsets;
  language?: CheckLanguage;
}) {
  const [active, setActive] = useState(false);
  const [focusFieldId, setFocusFieldId] = useState<string | null>(null);
  const shell = SHELL[language];

  // Focus follows the visitor into the real form after a keyboard focus, a
  // mouse press or a submit, and never after a touch, which may start a scroll.
  const shellIntent = active
    ? {}
    : {
        onPointerDownCapture: (event: PointerEvent<HTMLDivElement>) =>
          setFocusFieldId(event.pointerType === "mouse" ? formFieldId(event.target) : null),
        onFocusCapture: (event: FocusEvent<HTMLDivElement>) =>
          setFocusFieldId(formFieldId(event.target)),
        onSubmitCapture: () => setFocusFieldId(GRAHA_CHART_CHECK_FIELD_IDS.date),
      };

  return (
    <div
      translate="no"
      data-graha-hub-check-slot=""
      className={`flex flex-col ${GRAHA_CHART_FORM_RESERVE}`}
      {...shellIntent}
    >
      {active ? (
        <ShellProps.Provider value={shell}>
          <GrahaChartCheck offsets={offsets} language={language} focusFieldId={focusFieldId} />
        </ShellProps.Provider>
      ) : (
        <BirthToolFormShell {...shell} onActivate={() => setActive(true)} />
      )}
    </div>
  );
}
